import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { TokenInput } from '../TokenInput';
import { useDarkMode } from '../../hooks/useDarkMode';
import { FeatureTooltip } from '../onboarding/FeatureTooltip';
import type { FilterConfig } from './PortfolioFilters';
import type { Coin } from '../../types';

interface PortfolioToolbarProps {
  // Token addition
  onAddToken: (coinId: string, symbol: string) => void;
  coins: Coin[];
  tokensCount: number;

  // Filters
  filters: FilterConfig;
  onFiltersChange: (filters: FilterConfig) => void;
  onFiltersReset: () => void;

  // Import/Export actions
  onImportJSON: () => void;
  onImportCSV?: () => void;  // CSV import from exchanges
  onImportQR?: () => void;  // QR Code functionality temporarily disabled
  onExportCSV: () => void;
  onExportJSON: () => void;
  onTaxExport: () => void;
  onLoadDemo?: () => void;  // Load demo portfolio

  // View options (expert mode)
  userMode: 'simple' | 'expert';
  showBTCComparison: boolean;
  onToggleBTCComparison: () => void;

  // Snapshot (expert mode)
  onSnapshot?: () => void;

  // Investment Simulator
  onInvestmentSimulator?: () => void;
}

/**
 * PortfolioToolbar - Unified toolbar for portfolio management
 * Combines token addition, search, filters, and actions in a compact bar
 */
export function PortfolioToolbar({
  onAddToken,
  coins,
  tokensCount,
  filters,
  onFiltersChange,
  onFiltersReset,
  onImportJSON,
  onImportCSV,
  onImportQR,
  onExportCSV,
  onExportJSON,
  onTaxExport,
  onLoadDemo,
  userMode,
  showBTCComparison,
  onToggleBTCComparison,
  onSnapshot,
  onInvestmentSimulator,
}: PortfolioToolbarProps) {
  const { t } = useTranslation();
  const [isDark] = useDarkMode();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const hasActiveFilters =
    filters.search !== '' ||
    filters.profitLoss !== 'all' ||
    filters.minValue !== '' ||
    filters.maxValue !== '' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '';

  // Gradient colors for light/dark mode
  const gradientStyle = isDark
    ? {
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.1) 50%, rgba(236, 72, 153, 0.08) 100%)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
      }
    : {
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(236, 72, 153, 0.03) 100%)',
        border: '1px solid rgba(59, 130, 246, 0.15)',
      };

  return (
    <div
      style={{
        marginBottom: '20px',
        padding: '14px 18px',
        borderRadius: '12px',
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
          : '0 4px 20px rgba(59, 130, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
        ...gradientStyle
      }}
    >
      {/* Main toolbar row */}
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Token Input - Primary action */}
        <div style={{ flex: '1 1 300px', minWidth: '200px', maxWidth: '400px' }}>
          <TokenInput
            onAdd={onAddToken}
            coins={coins}
            placeholder={t('portfolioDashboard.searchOrAddToken')}
            compact
          />
        </div>

        {/* Quick filters - Only show when tokens exist */}
        {tokensCount > 0 && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Search in portfolio */}
            <div style={{ position: 'relative', minWidth: '160px' }}>
              <span style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted)',
                display: 'flex'
              }}>
                <Icons.Search size={14} />
              </span>
              <input
                type="text"
                placeholder={t('portfolioDashboard.filterTokens')}
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '32px',
                  padding: '6px 10px 6px 32px',
                  fontSize: '13px',
                  height: '34px'
                }}
              />
            </div>

            {/* P/L Filter Pills */}
            <div style={{
              display: 'flex',
              gap: '2px',
              background: 'var(--bg-secondary)',
              borderRadius: '6px',
              padding: '2px'
            }}>
              <button
                onClick={() => onFiltersChange({ ...filters, profitLoss: 'all' })}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  background: filters.profitLoss === 'all' ? 'var(--accent)' : 'transparent',
                  color: filters.profitLoss === 'all' ? 'white' : 'var(--text)'
                }}
              >
                {t('portfolioDashboard.all')}
              </button>
              <button
                onClick={() => onFiltersChange({ ...filters, profitLoss: 'profit' })}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  background: filters.profitLoss === 'profit' ? '#10b981' : 'transparent',
                  color: filters.profitLoss === 'profit' ? 'white' : 'var(--text)'
                }}
              >
                {t('portfolioDashboard.profit')}
              </button>
              <button
                onClick={() => onFiltersChange({ ...filters, profitLoss: 'loss' })}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  background: filters.profitLoss === 'loss' ? '#ef4444' : 'transparent',
                  color: filters.profitLoss === 'loss' ? 'white' : 'var(--text)'
                }}
              >
                {t('portfolioDashboard.loss')}
              </button>
            </div>

            {/* Sort dropdown */}
            <select
              aria-label="Trier le portfolio"
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-') as [FilterConfig['sortBy'], FilterConfig['sortOrder']];
                onFiltersChange({ ...filters, sortBy, sortOrder });
              }}
              style={{
                padding: '6px 10px',
                fontSize: '12px',
                height: '34px',
                minWidth: '120px'
              }}
            >
              <option value="value-desc">{t('portfolioDashboard.sortValueDesc')}</option>
              <option value="value-asc">{t('portfolioDashboard.sortValueAsc')}</option>
              <option value="pl-desc">{t('portfolioDashboard.sortPLDesc')}</option>
              <option value="pl-asc">{t('portfolioDashboard.sortPLAsc')}</option>
              <option value="plPct-desc">{t('portfolioDashboard.sortPLPctDesc')}</option>
              <option value="plPct-asc">{t('portfolioDashboard.sortPLPctAsc')}</option>
              <option value="name-asc">{t('portfolioDashboard.sortNameAsc')}</option>
              <option value="name-desc">{t('portfolioDashboard.sortNameDesc')}</option>
            </select>

            {/* Advanced filters toggle */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={hasActiveFilters ? 'btn-primary' : 'btn-ghost'}
              style={{
                padding: '6px 10px',
                fontSize: '12px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title={t('portfolioDashboard.advancedFilters')}
            >
              <Icons.Filter size={14} />
              {showAdvancedFilters ? <Icons.ChevronUp size={12} /> : <Icons.ChevronDown size={12} />}
            </button>

            {/* Reset filters */}
            {hasActiveFilters && (
              <button
                onClick={onFiltersReset}
                className="btn-ghost"
                style={{
                  padding: '6px',
                  height: '34px',
                  color: 'var(--danger)'
                }}
                title={t('portfolioDashboard.resetFilters')}
              >
                <Icons.X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: '1' }} />

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Load Demo Portfolio - Show prominently when portfolio is empty */}
          {onLoadDemo && (
            <button
              onClick={onLoadDemo}
              className="btn-ghost"
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: tokensCount === 0 ? 'rgba(139, 92, 246, 0.15)' : undefined,
                borderColor: tokensCount === 0 ? 'rgba(139, 92, 246, 0.3)' : undefined,
                color: tokensCount === 0 ? 'var(--primary)' : undefined,
              }}
              title={t('portfolioDashboard.loadDemo') || 'Load demo portfolio'}
            >
              <Icons.Play size={16} />
              <span>Demo</span>
            </button>
          )}

          {/* Import JSON */}
          <button
            onClick={onImportJSON}
            className="btn-ghost"
            style={{
              padding: '6px 12px',
              fontSize: '13px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title={t('portfolioDashboard.importJSON')}
          >
            <Icons.Upload size={16} />
            <span>{t('portfolioDashboard.import')}</span>
          </button>

          {/* Import CSV from exchanges (expert mode) */}
          {userMode === 'expert' && onImportCSV && (
            <button
              onClick={onImportCSV}
              className="btn-ghost"
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(139, 92, 246, 0.1)',
                borderColor: 'rgba(139, 92, 246, 0.3)',
              }}
              title={t('portfolioDashboard.importCSV', 'Import CSV from exchange')}
            >
              <Icons.FileText size={16} />
              <span>CSV</span>
            </button>
          )}

          {/* Export JSON */}
          <button
            onClick={onExportJSON}
            disabled={tokensCount === 0}
            className="btn-ghost"
            style={{
              padding: '6px 12px',
              fontSize: '13px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: tokensCount === 0 ? 0.5 : 1,
              cursor: tokensCount === 0 ? 'not-allowed' : 'pointer'
            }}
            title={t('portfolioDashboard.exportJSON')}
          >
            <Icons.Download size={16} />
            <span>{t('portfolioDashboard.export')}</span>
          </button>

          {/* Expert mode buttons: CSV, Taxes, BTC */}
          {userMode === 'expert' && (
            <>
              {/* QR Code scan - temporarily disabled */}
              {/* <button
                onClick={onImportQR}
                className="btn-ghost"
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  height: '34px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title={t('portfolioDashboard.scanQrCode')}
              >
                <Icons.Camera size={16} />
                <span>QR</span>
              </button> */}

              {/* Export CSV */}
              <FeatureTooltip featureId="export_csv" position="bottom">
                <button
                  onClick={onExportCSV}
                  disabled={tokensCount === 0}
                  className="btn-ghost"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    height: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: tokensCount === 0 ? 0.5 : 1,
                    cursor: tokensCount === 0 ? 'not-allowed' : 'pointer'
                  }}
                  title={t('portfolioDashboard.exportCSV')}
                >
                  <Icons.Download size={16} />
                  <span>CSV</span>
                </button>
              </FeatureTooltip>

              {/* Fiscal / Taxes */}
              <FeatureTooltip featureId="tax_report" position="bottom">
                <button
                  onClick={onTaxExport}
                  disabled={tokensCount === 0}
                  className="btn-ghost"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    height: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: tokensCount === 0 ? 0.5 : 1,
                    cursor: tokensCount === 0 ? 'not-allowed' : 'pointer'
                  }}
                  title={t('portfolioDashboard.fiscal')}
                >
                  <Icons.FileText size={16} />
                  <span>{t('portfolioDashboard.taxes')}</span>
                </button>
              </FeatureTooltip>

              {/* BTC Comparison toggle */}
              <FeatureTooltip featureId="btc_comparison" position="bottom">
                <button
                  onClick={onToggleBTCComparison}
                  disabled={tokensCount === 0}
                  className={showBTCComparison ? 'btn-primary' : 'btn-ghost'}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    height: '34px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    opacity: tokensCount === 0 ? 0.5 : 1,
                    cursor: tokensCount === 0 ? 'not-allowed' : 'pointer',
                    minWidth: 'auto',
                    width: 'auto',
                    flexShrink: 0
                  }}
                  title={t('portfolioDashboard.btcComparison')}
                >
                  <Icons.TrendingUp size={16} />
                  <span>BTC</span>
                </button>
              </FeatureTooltip>

              {/* Snapshot */}
              {onSnapshot && (
                <FeatureTooltip featureId="portfolio_snapshot" position="bottom">
                  <button
                    onClick={onSnapshot}
                    className="btn-ghost"
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      height: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    title={t('snapshots.takeSnapshot', 'Take a portfolio snapshot')}
                  >
                    <Icons.Camera size={16} />
                    <span>{t('snapshots.snapshot', 'Snapshot')}</span>
                  </button>
                </FeatureTooltip>
              )}

              {/* Investment Simulator - Recovery analysis */}
              {onInvestmentSimulator && (
                <FeatureTooltip featureId="investment_simulator" position="bottom">
                  <button
                    onClick={onInvestmentSimulator}
                    disabled={tokensCount === 0}
                    className="btn-ghost"
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      height: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      borderColor: 'rgba(16, 185, 129, 0.3)',
                      opacity: tokensCount === 0 ? 0.5 : 1,
                      cursor: tokensCount === 0 ? 'not-allowed' : 'pointer'
                    }}
                    title={t('investmentSimulator.title', 'Investment recovery simulator')}
                  >
                    <Icons.TrendingUp size={16} />
                    <span>{t('investmentSimulator.button', 'Simulator')}</span>
                  </button>
                </FeatureTooltip>
              )}
            </>
          )}
        </div>
      </div>

      {/* Advanced filters row (collapsible) */}
      {showAdvancedFilters && tokensCount > 0 && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid var(--border)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px'
        }}>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
              {t('portfolioDashboard.minValue')}
            </label>
            <input
              type="number"
              placeholder="0"
              value={filters.minValue}
              onChange={(e) => onFiltersChange({ ...filters, minValue: e.target.value })}
              style={{ width: '100%', fontSize: '13px', padding: '6px 10px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
              {t('portfolioDashboard.maxValue')}
            </label>
            <input
              type="number"
              placeholder="∞"
              value={filters.maxValue}
              onChange={(e) => onFiltersChange({ ...filters, maxValue: e.target.value })}
              style={{ width: '100%', fontSize: '13px', padding: '6px 10px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
              {t('portfolioDashboard.dateFrom')}
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onFiltersChange({ ...filters, dateFrom: e.target.value })}
              style={{ width: '100%', fontSize: '13px', padding: '6px 10px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
              {t('portfolioDashboard.dateTo')}
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => onFiltersChange({ ...filters, dateTo: e.target.value })}
              style={{ width: '100%', fontSize: '13px', padding: '6px 10px' }}
            />
          </div>
        </div>
      )}

      {/* Empty state hint */}
      {tokensCount === 0 && (
        <div style={{
          marginTop: '12px',
          padding: '8px 12px',
          background: 'rgba(59, 130, 246, 0.05)',
          borderRadius: '6px',
          fontSize: '12px',
          color: 'var(--muted)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span style={{ color: '#3b82f6', flexShrink: 0, display: 'flex' }}>
            <Icons.Lightbulb size={14} />
          </span>
          <span>{t('portfolioDashboard.tipStartWith')}</span>
        </div>
      )}
    </div>
  );
}
